const EMPTY_ARRAY: readonly [] = []

export function safeClone<T>(array: ReadonlyArray<T>): T[] {
  return [...(array ?? [])]
}

/**
 * Retorna um array vazio imutável com tipagem. Exemplo:
 *
 * ```ts
 * const myList = someList ?? emptyArrayOf<SomeListModel>();
 * ```
 */
export const emptyArrayOf = <T>(): ReadonlyArray<T> => EMPTY_ARRAY as ReadonlyArray<T>

/**
 * Particiona um array em dois grupos baseado em um predicado.
 *
 * Itera sobre o array fornecido e separa os elementos em dois arrays distintos:
 * o primeiro contém os elementos que satisfazem o predicado, e o segundo contém
 * os elementos que não satisfazem.
 *
 * Aceita `null`/`undefined` como entrada, retornando `[[], []]` — mesmo
 * comportamento do `partition` do lodash, evitando regressões nos pontos que
 * passam coleções potencialmente indefinidas.
 *
 * O predicado pode retornar qualquer valor truthy/falsy (não apenas `boolean`),
 * sendo avaliado por veracidade — mesmo comportamento do `partition` do lodash.
 *
 * @template T - O tipo dos elementos no array
 * @param array - O array a ser particionado (aceita `null`/`undefined`)
 * @param predicate - Função que determina o critério de separação dos itens
 * @returns Uma tupla onde o primeiro elemento é o array com itens que satisfazem
 *          o predicado e o segundo elemento é o array com itens que não satisfazem
 */
export function partition<T>(array: ReadonlyArray<T> | null | undefined, predicate: (item: T) => unknown): [T[], T[]] {
  const groupA: T[] = []
  const groupB: T[] = []

  for (const item of array ?? []) {
    if (predicate(item)) {
      groupA.push(item)
    } else {
      groupB.push(item)
    }
  }

  return [groupA, groupB]
}

type Falsy = false | null | undefined | 0 | ''

/**
 * Remove todos os valores falsy (`false`, `null`, `undefined`, `0`, `''`, `NaN`)
 * de um array, substituindo o `compact` do lodash por implementação nativa.
 *
 * @template T - O tipo dos elementos restantes no array
 * @param array - O array a ser filtrado (aceita `null`/`undefined`, retornando `[]`)
 * @returns Um novo array contendo apenas os valores truthy
 */
export function compact<T>(array: ReadonlyArray<T | Falsy> | null | undefined): T[] {
  return (array ?? []).filter(Boolean) as T[]
}

/**
 * Remove itens duplicados por `id`, mantendo a primeira ocorrência e a ordem original.
 * Itens nulos ou indefinidos são ignorados.
 */
export const dedupById = <T extends { id: ID }>(items: ReadonlyArray<T | null | undefined>): T[] => {
  const seen = new Set<ID>()
  const out: T[] = []
  items.forEach((item) => {
    if (item && !seen.has(item.id)) {
      seen.add(item.id)
      out.push(item)
    }
  })
  return out
}

/**
 * Retorna os itens cujo `id` não está presente no conjunto informado, preservando a ordem original.
 */
export const excludeIds = <T extends { id: ID }>(items: ReadonlyArray<T>, ids: ReadonlySet<ID>): T[] =>
  items.filter((it) => !ids.has(it.id))

/**
 * Verifica se `item` é o único elemento do array que satisfaz o predicado — ou seja,
 * se `item` satisfaz o predicado e nenhum outro item (comparado por referência) também satisfaz.
 *
 * Útil para decidir se remover um item é a última ocorrência que sustenta alguma condição
 * dependente do grupo, evitando disparar um efeito (ex.: um aviso de exclusão) quando ainda
 * restam outros itens que satisfazem o mesmo predicado.
 *
 * @template T - O tipo dos elementos no array
 * @param array - O array a ser verificado (aceita `null`/`undefined`)
 * @param item - O item cuja unicidade em relação ao predicado será verificada
 * @param predicate - Função que determina se um item satisfaz a condição observada
 * @returns `true` se `item` satisfaz o predicado e nenhum outro item do array também satisfaz
 */
export function isOnlyMatching<T>(
  array: ReadonlyArray<T> | null | undefined,
  item: T,
  predicate: (item: T) => unknown
): boolean {
  return !!predicate(item) && (array ?? []).every((other) => other === item || !predicate(other))
}
